import { Button } from "@/components/ui/UIElements/Button";
import React, { useState, useEffect, useId } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "../../";
import CopyTimesDropdown from "./CopyTimesDropdown";

const ScheduleStep = ({ schedule, onScheduleChange, errors = {} }) => {
  const { t } = useTranslation("common");
  const scheduleId = useId();
  const [localSchedule, setLocalSchedule] = useState(schedule || []);
  const [activeCopyDay, setActiveCopyDay] = useState(null);
  const [copyTargets, setCopyTargets] = useState([]);

  const days = [
    t("schedule.days.monday"),
    t("schedule.days.tuesday"),
    t("schedule.days.wednesday"),
    t("schedule.days.thursday"),
    t("schedule.days.friday"),
    t("schedule.days.saturday"),
    t("schedule.days.sunday"),
  ];

  // Initialize schedule from prop only on mount
  useEffect(() => {
    if (schedule && schedule.length > 0) {
      setLocalSchedule(schedule);
    } else {
      const defaultSchedule = days.map(() => ({
        enabled: true,
        lapses: [
          { open: { hour: 0, minute: 0 }, close: { hour: 23, minute: 59 } },
        ],
      }));
      setLocalSchedule(defaultSchedule);
    }
  }, []); // Only run on mount

  const toggleDayEnabled = (dayIndex) => {
    const newSchedule = localSchedule.map((day, idx) => {
      if (idx !== dayIndex) return day;
      return {
        ...day,
        enabled: !day.enabled,
      };
    });
    setLocalSchedule(newSchedule);
    onScheduleChange(newSchedule);
  };

  const addLapse = (dayIndex) => {
    const newSchedule = localSchedule.map((day, idx) => {
      if (idx !== dayIndex) return day;
      return {
        ...day,
        lapses: [
          ...day.lapses,
          { open: { hour: 0, minute: 0 }, close: { hour: 23, minute: 59 } },
        ],
      };
    });
    setLocalSchedule(newSchedule);
    onScheduleChange(newSchedule);
  };

  const removeLapse = (dayIndex, lapseIndex) => {
    const day = localSchedule[dayIndex];
    if (day.lapses.length > 1) {
      const newSchedule = localSchedule.map((d, idx) => {
        if (idx !== dayIndex) return d;
        return {
          ...d,
          lapses: d.lapses.filter((_, lIdx) => lIdx !== lapseIndex),
        };
      });
      setLocalSchedule(newSchedule);
      onScheduleChange(newSchedule);
    }
  };

  const updateLapse = (dayIndex, lapseIndex, field, value) => {
    const newSchedule = localSchedule.map((day, idx) => {
      if (idx !== dayIndex) return day;

      return {
        ...day,
        lapses: day.lapses.map((lapse, lIdx) => {
          if (lIdx !== lapseIndex) return lapse;

          return {
            ...lapse,
            [field]: { ...value },
          };
        }),
      };
    });

    setLocalSchedule(newSchedule);
    onScheduleChange(newSchedule);
  };

  const toggleCopyTarget = (targetIndex) => {
    setCopyTargets((prev) =>
      prev.includes(targetIndex)
        ? prev.filter((i) => i !== targetIndex)
        : [...prev, targetIndex],
    );
  };

  const applyCopyToTargets = (sourceDayIndex) => {
    const source = localSchedule[sourceDayIndex];
    if (!source) return;
    const clonedLapses = (source.lapses || []).map((l) => ({
      open: { ...l.open },
      close: { ...l.close },
    }));

    const newSchedule = [...localSchedule];
    copyTargets.forEach((targetIndex) => {
      if (targetIndex === sourceDayIndex) return;
      const target = newSchedule[targetIndex] || { enabled: true, lapses: [] };
      newSchedule[targetIndex] = {
        ...target,
        enabled: source.enabled,
        lapses: clonedLapses,
      };
    });

    setLocalSchedule(newSchedule);
    onScheduleChange(newSchedule);
    setActiveCopyDay(null);
    setCopyTargets([]);
  };

  const validateLapses = (lapses) => {
    // Sort lapses by open time
    const sortedLapses = [...lapses].sort((a, b) => {
      const aMinutes = a.open.hour * 60 + a.open.minute;
      const bMinutes = b.open.hour * 60 + b.open.minute;
      return aMinutes - bMinutes;
    });

    for (let i = 0; i < sortedLapses.length - 1; i++) {
      const current = sortedLapses[i];
      const next = sortedLapses[i + 1];

      const currentEndMinutes = current.close.hour * 60 + current.close.minute;
      const nextStartMinutes = next.open.hour * 60 + next.open.minute;

      if (currentEndMinutes >= nextStartMinutes) {
        return {
          isValid: false,
          message: t("schedule.overlapError", {
            count: i + 1,
            close: formatTime(current.close),
            open: formatTime(next.open),
          }),
        };
      }
    }

    return { isValid: true };
  };

  const formatTime = (time) => {
    return `${time.hour.toString().padStart(2, "0")}:${time.minute.toString().padStart(2, "0")}`;
  };

  const formatTimeForInput = (time) => {
    return `${time.hour.toString().padStart(2, "0")}:${time.minute.toString().padStart(2, "0")}`;
  };

  const parseTimeFromInput = (timeString) => {
    const [hours, minutes] = timeString.split(":").map(Number);
    return { hour: hours || 0, minute: minutes || 0 };
  };

  const handleTimeChange = (dayIndex, lapseIndex, field, timeString) => {
    const time = parseTimeFromInput(timeString);
    updateLapse(dayIndex, lapseIndex, field, time);
  };

  const getValidationError = (dayIndex) => {
    const daySchedule = localSchedule[dayIndex];
    if (!daySchedule || !daySchedule.enabled) return null;

    const validation = validateLapses(daySchedule.lapses);
    return validation.isValid ? null : validation.message;
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h3 className="mb-3 text-base font-medium text-white sm:mb-4 sm:text-lg">
          {t("schedule.businessHours")}
        </h3>
        <p className="text-muted-foreground mb-4 text-xs sm:mb-6 sm:text-sm">
          {t("schedule.description")}
        </p>
      </div>

      {days.map((day, dayIndex) => {
        const daySchedule = localSchedule[dayIndex] || {
          enabled: true,
          lapses: [],
        };
        const validationError = getValidationError(dayIndex);

        return (
          <div
            key={day}
            className="border-border bg-secondary rounded-lg border p-3 sm:p-4"
          >
            <div className="relative mb-3 flex items-center justify-between sm:mb-4">
              <div className="flex items-center space-x-2 sm:space-x-3">
                <Button
                  variant="ghost"
                  aria-label={t(
                    daySchedule.enabled
                      ? "schedule.disableDay"
                      : "schedule.enableDay",
                    { day },
                  )}
                  onClick={() => toggleDayEnabled(dayIndex)}
                  className={`flex h-5 w-5 items-center justify-center rounded border-2 sm:h-6 sm:w-6 ${
                    daySchedule.enabled
                      ? "border-primary bg-primary"
                      : "border-border bg-muted"
                  }`}
                >
                  {daySchedule.enabled && (
                    <Icon
                      name="Check"
                      className="h-3 w-3 text-white sm:h-4 sm:w-4"
                    />
                  )}
                </Button>
                <span
                  className={`text-sm font-medium sm:text-base ${daySchedule.enabled ? "text-white" : "text-muted-foreground"}`}
                >
                  {day}
                </span>
              </div>

              <div className="flex items-center space-x-2">
                {daySchedule.enabled && (
                  <Button
                    variant="ghost"
                    aria-label={t("schedule.addPeriodFor", { day })}
                    onClick={() => addLapse(dayIndex)}
                    className="text-primary hover:text-primary flex items-center space-x-1 text-xs sm:text-sm"
                  >
                    <Icon name="Plus" className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span className="hidden sm:inline">
                      {t("schedule.addPeriod")}
                    </span>
                    <span className="sm:hidden">{t("schedule.addShort")}</span>
                  </Button>
                )}

                <Button
                  variant="ghost"
                  onClick={() =>
                    setActiveCopyDay(
                      activeCopyDay === dayIndex ? null : dayIndex,
                    )
                  }
                  className="border-border text-foreground rounded border p-1.5 hover:text-white sm:p-2"
                  title={t("schedule.copyTimes")}
                  aria-label={t("schedule.copyTimes")}
                >
                  <Icon name="Copy" className="h-3 w-3 sm:h-4 sm:w-4" />
                </Button>

                {activeCopyDay === dayIndex && (
                  <CopyTimesDropdown
                    days={days}
                    excludeIndex={dayIndex}
                    selectedTargets={copyTargets}
                    onToggleTarget={toggleCopyTarget}
                    onApply={() => applyCopyToTargets(dayIndex)}
                    onCancel={() => {
                      setActiveCopyDay(null);
                      setCopyTargets([]);
                    }}
                  />
                )}
              </div>
            </div>

            {daySchedule.enabled && (
              <div className="space-y-2 sm:space-y-3">
                {daySchedule.lapses.map((lapse, lapseIndex) => (
                  <div
                    key={lapseIndex}
                    className="bg-muted flex flex-col gap-2 rounded-lg p-2 sm:flex-row sm:items-center sm:gap-4 sm:p-3"
                  >
                    <div className="flex-1">
                      <label
                        htmlFor={`${scheduleId}-${dayIndex}-${lapseIndex}-open`}
                        className="text-muted-foreground mb-1 block text-xs"
                      >
                        {t("schedule.open")}
                      </label>
                      <input
                        type="time"
                        id={`${scheduleId}-${dayIndex}-${lapseIndex}-open`}
                        aria-label={t("schedule.openTime", {
                          day,
                          count: lapseIndex + 1,
                        })}
                        value={formatTimeForInput(lapse.open)}
                        onChange={(e) =>
                          handleTimeChange(
                            dayIndex,
                            lapseIndex,
                            "open",
                            e.target.value,
                          )
                        }
                        className="border-border bg-secondary focus:ring-ring w-full rounded border px-2 py-1.5 text-xs text-white focus:ring-2 focus:outline-none sm:px-3 sm:py-2 sm:text-sm"
                      />
                    </div>

                    <div className="flex-1">
                      <label
                        htmlFor={`${scheduleId}-${dayIndex}-${lapseIndex}-close`}
                        className="text-muted-foreground mb-1 block text-xs"
                      >
                        {t("schedule.close")}
                      </label>
                      <input
                        type="time"
                        id={`${scheduleId}-${dayIndex}-${lapseIndex}-close`}
                        aria-label={t("schedule.closeTime", {
                          day,
                          count: lapseIndex + 1,
                        })}
                        value={formatTimeForInput(lapse.close)}
                        onChange={(e) =>
                          handleTimeChange(
                            dayIndex,
                            lapseIndex,
                            "close",
                            e.target.value,
                          )
                        }
                        className="border-border bg-secondary focus:ring-ring w-full rounded border px-2 py-1.5 text-xs text-white focus:ring-2 focus:outline-none sm:px-3 sm:py-2 sm:text-sm"
                      />
                    </div>

                    <div className="flex items-center pt-4">
                      <Button
                        variant="ghost"
                        aria-label={t("schedule.removePeriodFor", { day })}
                        onClick={() => removeLapse(dayIndex, lapseIndex)}
                        disabled={daySchedule.lapses.length === 1}
                        className={`p-1.5 transition-colors sm:p-2 ${
                          daySchedule.lapses.length > 1
                            ? "text-destructive hover:text-destructive cursor-pointer"
                            : "text-muted-foreground cursor-not-allowed opacity-50"
                        }`}
                        title={
                          daySchedule.lapses.length > 1
                            ? t("schedule.removePeriod")
                            : t("schedule.atLeastOnePeriod")
                        }
                      >
                        <Icon name="Trash2" className="h-3 w-3 sm:h-4 sm:w-4" />
                      </Button>
                    </div>
                  </div>
                ))}

                {validationError && (
                  <div className="border-destructive bg-destructive/20 text-destructive rounded border p-2 text-xs sm:text-sm">
                    {validationError}
                  </div>
                )}
              </div>
            )}

            {!daySchedule.enabled && (
              <div className="text-muted-foreground text-xs italic sm:text-sm">
                {t("schedule.closedOn", { day })}
              </div>
            )}
          </div>
        );
      })}

      {errors.schedule && (
        <div className="border-destructive bg-destructive/20 text-destructive rounded border p-2 text-xs sm:text-sm">
          {errors.schedule}
        </div>
      )}
    </div>
  );
};

export default ScheduleStep;
